import { computed, inject } from '@angular/core';
import { OrderApi } from '@app/api/order.api';
import {
  OrderDto,
  OrderPointDto,
  OrderPointRequest,
  OrderRequest,
  OrderShareDto,
  OrderStatuses,
  ShareOrderRequest,
  UnshareOrderRequest,
} from '@app/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  concatMap,
  EMPTY,
  forkJoin,
  from,
  map,
  Observable,
  pipe,
  switchMap,
  tap,
  toArray,
} from 'rxjs';

interface OrderState {
  orders: OrderDto[];
  isLoading: boolean;
  isSubmitting: boolean;
  orderPointsById: Record<number, OrderPointDto[]>;
  orderFilesById: Record<number, File[]>;
}

const initialState: OrderState = {
  orders: [],
  isLoading: false,
  isSubmitting: false,
  orderPointsById: {},
  orderFilesById: {},
};

export const OrdersStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    activeOrders: computed(() =>
      store
        .orders()
        .filter((order) => order.status === OrderStatuses.IN_PROGRESS),
    ),
    openOrders: computed(() =>
      store.orders().filter((order) => order.status !== OrderStatuses.DONE),
    ),
    closedOrders: computed(() =>
      store.orders().filter((order) => order.status === OrderStatuses.DONE),
    ),
  })),

  withMethods((store) => {
    const orderApi = inject(OrderApi);

    const loadOrders = rxMethod<void | { onRefresh?: () => void }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),

        switchMap((options) =>
          forkJoin({
            normal: orderApi.getOrdersNoCache$(true),
            shared: orderApi.getSharedOrders$() as Observable<OrderShareDto[]>,
          }).pipe(
            map(({ normal, shared }) => {
              const mappedShared = shared.map((s) => ({
                ...s.order,
                sharedId: s.id,
                externalOrderId: s.externalOrderId,
                internalOrderId: s.internalOrderId,
              }));

              return {
                orders: [...normal, ...mappedShared],
                onRefresh: options?.onRefresh,
              };
            }),
          ),
        ),

        tap(({ orders, onRefresh }) => {
          patchState(store, {
            orders,
            isLoading: false,
          });

          onRefresh?.();
        }),
      ),
    );

    const createOrder = rxMethod<{
      order: OrderRequest;
      routes: OrderPointRequest[];
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ order, routes, onSubmit, onRefresh }) =>
          orderApi.addOrder$(order).pipe(
            switchMap(({ id }) =>
              from(routes).pipe(
                map((route) => ({ ...route, orderId: id })),
                concatMap((route) => orderApi.addOrderPoint$(id, route)),
                toArray(),
                map(() => ({ onSubmit, onRefresh })),
              ),
            ),
          ),
        ),
        tap(({ onSubmit, onRefresh }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();
          loadOrders({ onRefresh });
        }),
      ),
    );

    const editOrder = rxMethod<{
      orderId: number;
      order: OrderRequest;
      routes: OrderPointRequest[];
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap((request) =>
          orderApi.updateOrder$(request.orderId, request.order).pipe(
            switchMap(({ id }) =>
              from(request.routes).pipe(
                concatMap((route) =>
                  orderApi.updateOrderPoint$(id, route.id, route),
                ),
                toArray(),
                map(() => request),
              ),
            ),
          ),
        ),
        tap(({ orderId, onSubmit, onRefresh }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();
          loadOrderPoints(orderId);
          loadOrders({ onRefresh });
        }),
        catchError(() => {
          patchState(store, { isSubmitting: false });

          return EMPTY;
        }),
      ),
    );

    const startOrder = rxMethod<{
      orderId: number;
      onRefresh: () => void;
    }>(
      pipe(
        switchMap(({ orderId, onRefresh }) =>
          orderApi.startOrder(orderId).pipe(map(() => ({ onRefresh }))),
        ),
        tap(({ onRefresh }) => loadOrders({ onRefresh })),
      ),
    );

    const shareOrder = rxMethod<{
      request: ShareOrderRequest;
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request: { orderId, email }, onSubmit, onRefresh }) =>
          orderApi.shareOrder$({ orderId, email }).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap(() => {
              patchState(store, { isSubmitting: false });
              onSubmit();
              loadOrders({ onRefresh });
            }),
          ),
        ),
      ),
    );

    const unshareOrder = rxMethod<{
      request: UnshareOrderRequest;
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request: { id, orderId }, onSubmit, onRefresh }) =>
          orderApi
            .unShareOrder$({ id, orderId })
            .pipe(map(() => ({ onSubmit, onRefresh }))),
        ),
        tap(({ onSubmit, onRefresh }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();
          loadOrders({ onRefresh });
        }),
      ),
    );

    const cancelOrder = rxMethod<{
      orderId: number;
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ orderId, onSubmit, onRefresh }) =>
          orderApi
            .cancelOrder(orderId)
            .pipe(map(() => ({ onSubmit, onRefresh }))),
        ),
        tap(({ onSubmit, onRefresh }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();
          loadOrders({ onRefresh });
        }),
      ),
    );

    const deleteOrder = rxMethod<{
      orderId: number;
      onSubmit: () => void;
      onRefresh: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ orderId, onSubmit, onRefresh }) =>
          orderApi
            .removeOrder$(orderId)
            .pipe(map(() => ({ onSubmit, onRefresh }))),
        ),
        tap(({ onSubmit, onRefresh }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();
          loadOrders({ onRefresh });
        }),
      ),
    );

    const loadOrderPoints = rxMethod<number>(
      pipe(
        switchMap((orderId) =>
          orderApi
            .getOrderPoints$(orderId)
            .pipe(map((points) => ({ orderId, points }))),
        ),

        tap(({ orderId, points }) =>
          patchState(store, {
            orderPointsById: {
              ...store.orderPointsById(),
              [orderId]: points,
            },
          }),
        ),
      ),
    );

    const getOrderPointsById = (orderId: number) =>
      computed(() => store.orderPointsById()[orderId] ?? []);

    const clearOrders = () => patchState(store, initialState);

    return {
      loadOrders,
      createOrder,
      editOrder,
      loadOrderPoints,
      getOrderPointsById,
      startOrder,
      shareOrder,
      unshareOrder,
      cancelOrder,
      deleteOrder,
      clearOrders,
    };
  }),
);
